import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import { getAuthToken } from "../../utils/getAuthToken";

// Same service the rest of the Calls page talks to (see CallLogsTable.jsx).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Mirrors the server's cap on one zip (callingRecordingController.js).
export const MAX_SELECTED_RECORDINGS = 50;

// Recordings are private: the endpoints need the Bearer token, which a plain <audio src> or <a href>
// can't send. So the file is fetched with the token and handed to the player/download as a blob URL.
async function fetchRecordingBlob(callId, { download = false } = {}) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}/api/calling/recordings/${callId}/file`, {
    headers: { Authorization: `Bearer ${token}` },
    params: download ? { download: 1 } : undefined,
    responseType: "blob",
  });
  return res.data;
}

async function fetchRecordingsZip(callIds) {
  const token = await getAuthToken();
  const res = await axios.post(
    `${SERVER_DOMAIN}/api/calling/recordings/download`,
    { callIds },
    { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, responseType: "blob" }
  );
  return res.data;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// e.g. 2026-10-29_14-05_inbound_Ahmed-Khan.m4a — readable when it lands in a Downloads folder.
function recordingFileName(row) {
  const when = new Date(row.startedAt);
  const stamp = isNaN(when.getTime()) ? "call" : when.toISOString().slice(0, 16).replace("T", "_").replace(":", "-");
  const clean = (v) => String(v || "").normalize("NFKD").replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  const who = clean(row.contactName) || clean(row.waNumber) || "unknown"; // a name in Urdu/Arabic script cleans to "", so use the number
  return `${stamp}_${row.direction}_${who}.m4a`;
}

function recordingErrorMessage(err, fallback) {
  return err?.response?.status === 404 ? "This recording isn't available" : fallback;
}

export const hasPlayableRecording = (row) => row.recording?.status === "persisted";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Selection tick for a row — a blank spacer for rows with nothing to select, so every row stays aligned. */
export function RecordingSelectBox({ row, selected, onToggle }) {
  return (
    <Box sx={{ width: 28, flexShrink: 0, display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
      {hasPlayableRecording(row) && (
        <Checkbox size="small" sx={{ p: 0.5 }} checked={selected} onChange={() => onToggle(row.callId)} inputProps={{ "aria-label": "Select recording" }} />
      )}
    </Box>
  );
}

/** Play + download buttons for one call's recording (or a quiet "Processing…" while it is still being finished). */
export function RecordingRowActions({ row, onPlay }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation(); // the row itself opens the chat — these buttons must not
    setDownloading(true);
    try {
      saveBlob(await fetchRecordingBlob(row.callId, { download: true }), recordingFileName(row));
    } catch (err) {
      toast.error(recordingErrorMessage(err, "Failed to download the recording"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ minWidth: 76, display: "flex", justifyContent: "flex-end", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      {hasPlayableRecording(row) && (
        <>
          <Tooltip title={`Play recording${row.recording.sizeBytes ? ` (${formatSize(row.recording.sizeBytes)})` : ""}`}>
            <IconButton size="small" aria-label="Play recording" onClick={() => onPlay(row)}>
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download recording">
            <span>
              <IconButton size="small" aria-label="Download recording" onClick={handleDownload} disabled={downloading}>
                {downloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      {row.recording?.status === "pending" && (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Processing…</Typography>
      )}
    </Box>
  );
}

/** Select-all + "Download selected" bar. Only rendered when recording is on for the workspace. */
export function RecordingToolbar({ rows, selectedIds, onSelectAll, onClear }) {
  const [downloading, setDownloading] = useState(false);
  const selectable = rows.filter(hasPlayableRecording);
  const count = selectedIds.size;

  const handleDownloadSelected = async () => {
    setDownloading(true);
    try {
      const blob = await fetchRecordingsZip([...selectedIds]);
      saveBlob(blob, `call-recordings-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (err) {
      toast.error(recordingErrorMessage(err, "Failed to download the selected recordings"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, minHeight: 36 }}>
      <Checkbox
        size="small"
        sx={{ p: 0.5 }}
        disabled={selectable.length === 0}
        checked={selectable.length > 0 && count >= Math.min(selectable.length, MAX_SELECTED_RECORDINGS)}
        indeterminate={count > 0 && count < Math.min(selectable.length, MAX_SELECTED_RECORDINGS)}
        onChange={(e) => (e.target.checked ? onSelectAll(selectable) : onClear())}
        inputProps={{ "aria-label": "Select all recordings" }}
      />
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {count > 0 ? `${count} recording${count === 1 ? "" : "s"} selected` : "Call recordings"}
      </Typography>
      {count > 0 && (
        <>
          <Button size="small" variant="contained" disabled={downloading} startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />} onClick={handleDownloadSelected}>
            {downloading ? "Preparing…" : "Download selected"}
          </Button>
          <Button size="small" color="inherit" disabled={downloading} onClick={onClear}>
            Clear
          </Button>
        </>
      )}
    </Box>
  );
}

/** Plays one recording in a dialog. The audio is fetched with the auth token and played from a blob URL. */
export function RecordingPlayerDialog({ row, onClose }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setAudioUrl(null);
    setFailed(false);
    fetchRecordingBlob(row.callId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setAudioUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(recordingErrorMessage(err, "Failed to load the recording"));
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [row]);

  const handleDownload = async () => {
    try {
      saveBlob(await fetchRecordingBlob(row.callId, { download: true }), recordingFileName(row));
    } catch (err) {
      toast.error(recordingErrorMessage(err, "Failed to download the recording"));
    }
  };

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{row ? row.contactName || row.waNumber : ""}</DialogTitle>
      <DialogContent>
        {row && (
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            {row.direction === "inbound" ? "Inbound" : "Outbound"} call · {new Date(row.startedAt).toLocaleString()}
            {row.agentName ? ` · ${row.agentName}` : ""}
          </Typography>
        )}
        {failed ? (
          <Typography color="error" sx={{ fontSize: 14 }}>
            This recording could not be loaded.
          </Typography>
        ) : audioUrl ? (
          <audio controls autoPlay src={audioUrl} style={{ width: "100%" }} />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5 }}>Caller on the left channel, agent on the right.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!audioUrl}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
